import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import cn from "classnames";

export type FlipWordsProps = Omit<React.ComponentProps<"span">, "children"> & {
    words: string[];
    duration?: number;
    letterDelay?: number;
    wordDelay?: number;
};

export const FlipWords = React.forwardRef<HTMLSpanElement, FlipWordsProps>(
    ({ words, duration = 3000, letterDelay = 0.05, wordDelay = 0.3, className, ...props }, ref) => {
        const [currentWord, setCurrentWord] = React.useState(words[0]);
        const [isAnimating, setIsAnimating] = React.useState<boolean>(false);

        const startAnimation = React.useCallback(() => {
            const word = words[words.indexOf(currentWord) + 1] || words[0];
            setCurrentWord(word);
            setIsAnimating(true);
        }, [currentWord, words]);

        React.useEffect(() => {
            if (!isAnimating) {
                const timeoutId = setTimeout(() => {
                    startAnimation();
                }, duration);
                return () => clearTimeout(timeoutId);
            }
        }, [isAnimating, duration, startAnimation]);

        return (
            <span ref={ref} data-slot="flip-words" {...props}>
                <AnimatePresence
                    onExitComplete={() => {
                        setIsAnimating(false);
                    }}
                >
                    <motion.span
                        key={currentWord}
                        // @ts-expect-error - initial is not a valid prop for motion.span
                        initial={{
                            opacity: 0,
                            y: 10,
                        }}
                        animate={{
                            opacity: 1,
                            y: 0,
                        }}
                        transition={{
                            type: "spring",
                            stiffness: 100,
                            damping: 10,
                        }}
                        exit={{
                            opacity: 0,
                            y: -40,
                            x: 40,
                            filter: "blur(8px)",
                            scale: 2,
                            position: "absolute",
                        }}
                        className={cn(
                            "inline-block relative text-left will-change-transform will-change-opacity will-change-filter leading-tight",
                            className
                        )}
                    >
                        {currentWord.split(" ").map((word, wordIndex) => (
                            <motion.span
                                key={`${word}-${wordIndex}`}
                                // @ts-expect-error - initial is not a valid prop for motion.span
                                initial={{ opacity: 0, y: 10, filter: "blur(8px)" }}
                                animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                                transition={{
                                    delay: wordIndex * wordDelay,
                                    duration: 0.3,
                                }}
                                className="inline-block whitespace-nowrap"
                            >
                                {word.split("").map((letter, letterIndex) => (
                                    <motion.span
                                        key={`${word}-${letterIndex}`}
                                        // @ts-expect-error - initial is not a valid prop for motion.span
                                        initial={{
                                            opacity: 0,
                                            y: 10,
                                            filter: "blur(8px)",
                                        }}
                                        animate={{
                                            opacity: 1,
                                            y: 0,
                                            filter: "blur(0px)",
                                        }}
                                        transition={{
                                            delay: wordIndex * wordDelay + letterIndex * letterDelay,
                                            duration: 0.2,
                                        }}
                                        className="inline-block will-change-transform will-change-opacity will-change-filter"
                                    >
                                        {letter}
                                    </motion.span>
                                ))}
                                <span className="inline-block">&nbsp;</span>
                            </motion.span>
                        ))}
                    </motion.span>
                </AnimatePresence>
            </span>
        );
    }
);

FlipWords.displayName = "FlipWords";
